import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LineReveal } from "./Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yWindow = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 220]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 pb-14 md:pt-28 md:pb-20">
      <motion.div
        style={{ y: yGlow }}
        className="glow-pulse absolute -top-24 right-[-30%] w-[420px] h-[420px] md:-top-40 md:right-[-10%] md:w-[700px] md:h-[700px] rounded-full pointer-events-none"
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(232,76,76,0.28) 0%, rgba(232,76,76,0.08) 45%, transparent 70%)" }} />
      </motion.div>

      <motion.div
        style={{ y: yWindow }}
        initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 0.4, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="lg:hidden absolute -right-[24%] -top-[2%] w-[52vw] max-w-[320px] aspect-[2/3] pointer-events-none"
        data-testid="hero-logo-watermark"
      >
        <div className="arch-shape w-full h-full relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(232,76,76,0.20) 0%, rgba(209,61,85,0.18) 35%, rgba(243,160,160,0.17) 70%, rgba(250,208,196,0.16) 100%)" }}>
          <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[6.5%] bg-white/70" />
          <div className="absolute left-0 right-0 h-[4.5%] bg-white/70" style={{ top: "36%" }} />
          <div className="absolute inset-0 glow-pulse" style={{ background: "radial-gradient(circle at 50% 28%, rgba(255,255,255,0.5) 0%, transparent 55%)" }} />
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 w-full grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center relative z-10">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#F3A0A0]/25 px-3.5 py-1.5 mb-6 md:mb-8 max-w-full"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-[#E84C4C] animate-pulse shrink-0" />
            <span className="text-[10px] sm:text-xs font-orbitron tracking-[0.2em] sm:tracking-[0.3em] text-[#E84C4C] whitespace-nowrap">A NONPROFIT DIGITAL MISSION</span>
          </motion.div>

          <h1 className="font-orbitron font-black text-[1.9rem] min-[400px]:text-[2.15rem] sm:text-5xl lg:text-6xl leading-[1.08] sm:leading-[1.05] tracking-tight text-[#120716]" data-testid="hero-headline">
            <LineReveal delay={0.3}>FREE WEBSITES</LineReveal>
            <LineReveal delay={0.45}>AND MARKETING</LineReveal>
            <LineReveal delay={0.6}>
              <span className="bg-gradient-to-r from-[#E84C4C] via-[#EE7A7A] to-[#F3A0A0] bg-clip-text text-transparent">
                FOR CHURCHES.
              </span>
            </LineReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 md:mt-8 max-w-xl text-[15px] sm:text-base md:text-lg text-[#120716]/70 leading-relaxed"
            data-testid="hero-story"
          >
            Somewhere right now a pastor is preaching a message worth hearing to a room of
            forty people, and nobody outside that room can find him. I started Digital Missions
            to change that. I design, build, and market for churches around the world at no cost
            to them, because I believe the Gospel deserves the best front door we know how to build.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-5 md:mt-6 italic font-light text-[#C13B3B] text-sm md:text-base border-l-2 border-[#E84C4C] pl-4"
          >
            "Go therefore and make disciples of all nations..." — Matthew 28:19
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href="#apply"
              data-testid="hero-apply-btn"
              className="w-full sm:w-auto text-center rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white font-semibold px-8 py-3.5 md:py-4 transition-all duration-300 hover:shadow-[0_0_36px_rgba(232,76,76,0.55)] hover:-translate-y-0.5"
            >
              Tell Me About Your Church
            </a>
            <a
              href="#donate"
              data-testid="hero-donate-btn"
              className="w-full sm:w-auto text-center rounded-full border border-[#E84C4C]/40 text-[#120716] font-semibold px-8 py-3.5 md:py-4 hover:bg-[#E84C4C]/10 hover:border-[#E84C4C] transition-all duration-300"
            >
              Help Me Build the Next One
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-10 md:mt-14 grid grid-cols-3 sm:flex sm:flex-wrap gap-4 sm:gap-8"
            data-testid="hero-stats"
          >
            {[["$0", "Ever charged to a church"], ["100%", "Donor funded"], ["1", "Mission: the Gospel"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-orbitron font-bold text-xl sm:text-2xl text-[#120716]">{n}</div>
                <div className="text-[10px] sm:text-xs tracking-widest uppercase text-[#120716]/50 mt-1 leading-snug">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y: yWindow }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:flex justify-center"
        >
          <div className="relative">
            <div className="arch-shape w-[320px] h-[480px] relative overflow-hidden" style={{ background: "linear-gradient(160deg, #E84C4C 0%, #d13d55 35%, #F3A0A0 70%, #FAD0C4 100%)", boxShadow: "0 0 90px rgba(232,76,76,0.4), inset 0 0 60px rgba(255,255,255,0.15)" }}>
              <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-5 bg-white" />
              <div className="absolute left-0 right-0 h-5 bg-white" style={{ top: "36%" }} />
              <div className="absolute inset-0 glow-pulse" style={{ background: "radial-gradient(circle at 50% 30%, rgba(255,255,255,0.45) 0%, transparent 55%)" }} />
            </div>
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[380px] h-16 rounded-full blur-2xl" style={{ background: "rgba(232,76,76,0.35)" }} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
